import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import {
  AmountFormatted,
  LIQUIDITY_BURNED_PRESET,
  NumericFormat,
} from '@root/shared/core/format'
import {
  BackingIcon,
  CoinsStackManyIcon,
  CrosshairIcon,
  DevBurnIcon,
  EditIcon,
  FileIcon,
  GearIcon,
  LiquidDropsIcon,
  ProfileIcon,
  RocketIcon,
  SniperIcon,
  SnowflakeIcon,
} from '@root/shared/ui-kit'

import { AuditContractItem } from './audit-contract-item'
import { TradingAuditContractItem } from './trading-audit-contract-item'
import { TradingAuditDevHolding } from './trading-audit-dev-holding'
import { TradingAuditOverview } from './trading-audit-overview'

import type { AuditOverviewItem } from './trading-audit-overview'
import type { IconProps } from '@blade/ui-kit'
import type { PairModel } from '@root/entities/pair'

const SHOW_UPDATE_AUTHORITY = Boolean(false)

const auditLabelIcon = (Icon: React.FC<IconProps>) => (
  <Icon size={16} color="var(--text-11)" />
)

type Props = {
  pair: PairModel
  variant: 'full' | 'short'
  makerFilter?: string | undefined
}

export const TradingAuditList: React.FC<Props> = ({
  pair,
  variant,
  makerFilter,
}) => {
  const { t } = useTranslation()
  const [snipersMeta] = useAtom(pair.snipersMeta)
  const [audit] = useAtom(pair.audit)

  const items = useMemo((): Array<AuditOverviewItem> => {
    const boolToYesNo = (value: boolean) =>
      value ? t('common.yes') : t('common.no')

    const checks: Array<AuditOverviewItem | undefined> = [
      variant === 'full'
        ? {
            icon: auditLabelIcon(EditIcon),
            label: t('trading.audit.is_mintable'),
            value: boolToYesNo(audit.checks.isMintable.value),
            intent: audit.checks.isMintable.status,
            tooltip: t('trading.audit.is_mintable.hint'),
          }
        : undefined,
      'isTokenDataMutable' in audit.checks && variant === 'full'
        ? {
            icon: auditLabelIcon(EditIcon),
            label: t('trading.audit.is_token_data_mutable'),
            value: boolToYesNo(audit.checks.isTokenDataMutable.value),
            intent: audit.checks.isTokenDataMutable.status,
            tooltip: t('trading.audit.is_token_data_mutable.hint'),
          }
        : undefined,
      variant === 'full'
        ? {
            icon: auditLabelIcon(SnowflakeIcon),
            label: t('trading.audit.is_freezable'),
            value: boolToYesNo(audit.checks.isFreezable.value),
            intent: audit.checks.isFreezable.status,
          }
        : undefined,

      variant === 'full' && SHOW_UPDATE_AUTHORITY
        ? {
            icon: auditLabelIcon(GearIcon),
            label: t('trading.audit.update_authority'),
            value:
              audit.checks.updateAuthority.value === 'None'
                ? t('trading.audit.update_authority.none')
                : audit.checks.updateAuthority.value.slice(-6),
            intent: audit.checks.updateAuthority.status,
          }
        : undefined,
      variant === 'full'
        ? {
            icon: auditLabelIcon(CoinsStackManyIcon),
            label: t('trading.audit.owner_balance'),
            value: (
              <AmountFormatted
                amount={audit.checks.ownerTokenBalance.amount}
                roundingMode="trunc"
              />
            ),
            intent: audit.checks.ownerTokenBalance.status,
          }
        : undefined,
      variant === 'full'
        ? {
            icon: auditLabelIcon(DevBurnIcon),
            label: t('trading.audit.lp_burned'),
            value: (
              <span>
                <Numeric
                  value={audit.checks.lpBurned.percent}
                  preset={LIQUIDITY_BURNED_PRESET}
                />
                %
              </span>
            ),
            intent: audit.checks.lpBurned.status,
            tooltip: t('trading.audit.lp_burned.hint'),
          }
        : undefined,
      snipersMeta
        ? {
            icon: auditLabelIcon(SniperIcon),
            label: (
              <SnipersLabel>
                <div>{t('trading.audit.snipers')}</div>
                <SnipersCount>
                  {snipersMeta.count}/{snipersMeta.total}
                </SnipersCount>
              </SnipersLabel>
            ),
            value: (
              <>
                <CrosshairIcon color="var(--amber-9)" size={16} />
                <span>
                  {NumericFormat.formatPreset(
                    snipersMeta.percentage * 100,
                    'percents',
                  )}
                  %
                </span>
                <span>({snipersMeta.count})</span>
              </>
            ),
            intent: 'Safe',
          }
        : undefined,
      variant === 'full'
        ? {
            icon: auditLabelIcon(BackingIcon),
            label: t('trading.audit.dev_holding'),
            value: <TradingAuditDevHolding pair={pair} />,
            intent: audit.checks.ownerTokenBalance.status,
          }
        : undefined,
      variant === 'full'
        ? {
            icon: auditLabelIcon(ProfileIcon),
            label: t('trading.audit.top_10_holders'),
            value: (
              <HoldersLabel>
                <span>
                  <Numeric
                    value={audit.checks.top10HoldersPercentage.percent}
                    preset="percents"
                  />
                  %
                </span>
              </HoldersLabel>
            ),
            intent: audit.checks.top10HoldersPercentage.status,
            tooltip: t('trading.audit.top_10_holders.hint'),
          }
        : undefined,
      {
        icon: auditLabelIcon(RocketIcon),
        label: t('trading.audit.ca_deployer'),
        value: (
          <TradingAuditContractItem
            address={audit.meta.deployerAddress}
            network={pair.base.network}
            filterActive={Boolean(
              makerFilter && makerFilter === audit.meta.deployerAddress,
            )}
          />
        ),
      },
      {
        icon: auditLabelIcon(LiquidDropsIcon),
        label: t('trading.audit.lp_creator'),
        value: (
          <TradingAuditContractItem
            address={audit.meta.lpCreatorAddress}
            network={pair.base.network}
            filterActive={Boolean(
              makerFilter && makerFilter === audit.meta.lpCreatorAddress,
            )}
          />
        ),
      },
      {
        icon: auditLabelIcon(FileIcon),
        label: t('pair.pair-address'),
        value: (
          <AuditContractItem
            address={pair.address}
            network={pair.base.network}
          />
        ),
      },
      {
        icon: auditLabelIcon(FileIcon),
        label: t('currency.token-address'),
        value: (
          <AuditContractItem
            address={pair.quote.address}
            network={pair.quote.network}
          />
        ),
      },
    ]

    return checks.filter((x) => x !== undefined)
  }, [
    variant,
    t,
    audit.checks,
    audit.meta.deployerAddress,
    audit.meta.lpCreatorAddress,
    snipersMeta,
    pair,
    makerFilter,
  ])

  return <TradingAuditOverview items={items} />
}

const _Badge = styled.span`
  color: var(--amber-9);
  background: rgba(255, 197, 61, 0.2);

  display: inline-flex;
  padding: 7px 6px;
  align-items: center;
  gap: 2px;
  border-radius: 6px;

  font-size: 8px;
  font-style: normal;
  font-weight: 700;
  line-height: 12px;
  letter-spacing: 0.16px;
  text-transform: uppercase;
`

const SnipersLabel = styled.div`
  display: grid;
  gap: 4px;
  align-items: center;
  grid-auto-flow: column;
`

const SnipersCount = styled.div`
  display: grid;
  align-items: center;
  padding-inline: 6px;

  width: 43px;
  height: 20px;
  flex-shrink: 0;

  color: var(--amber-9);
  background: rgba(var(--amber-9-rgb), 0.1);
  border-radius: 10px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
`

const HoldersLabel = styled.span<{ $clickable?: boolean }>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  display: grid;
  align-items: center;
  gap: 4px;
  grid-auto-flow: column;
`

/*

SOLANA:

- Is Mintable: checks: isMintable - value
- Is Token Data Mutable: checks: isTokenDataMutable - value
- Is Freezable: checks: isFreezable - value
- Update Authority: checks: updateAuthority - value
- LP Burned: checks: lpBurned - percent
- Owner Balance: checks: ownerTokenBalance - amount
- Top 10 Holders: checks: top10HoldersPercentage - percent
- CA Deployer: meta: deployerAddress
- LP Creator: meta: lpCreatorAddress

EVM:

- Is Mintable: checks: isMintable - value
- Is Freezable: checks: isFreezable - value
- Update Authority: checks: updateAuthority - value
- LP Burned: checks: lpBurned - percent
- Owner Balance: checks: ownerTokenBalance - amount
- Top 10 Holders: checks: top10HoldersPercentage - percent
- CA Deployer: meta: deployerAddress
- (!) Эти данные мы пока что не имеем без собственной ноды. LP Creator: meta: lpCreatorAddress - здесь адреса не будет, так что пишем Not Available

*/
